import React, { useCallback, useEffect, useMemo } from 'react';
import { withExperience } from '@helpcenter/analytics-components/src/experience';
import {
	ExperienceFailure as NotifyExperienceFailure,
	ExperienceSuccess as NotifyExperienceSuccess,
} from '@helpcenter/analytics-components/src/experience-notifier';
import { ExperienceName, ScreenName } from '@helpcenter/constants';
import ViewCustomPageResource from '@helpcenter/custom-page-resources/src/view-custom-page';
import { DocumentTitle } from '@helpcenter/document-title';
import ErrorBoundary from '@helpcenter/error-boundary/src/error-boundary';
import { isElementRendererSplitEnabled } from '@helpcenter/feature-flags';
import { FlexibleHomepageLoader } from '@helpcenter/flexible-homepage-loader';
import { addMetaToHead } from '@helpcenter/meta';
import { noop } from 'lodash';
import { LazySuspense } from 'react-loosely-lazy';
import { di } from 'react-magnetic-di';
import { graphql, usePreloadedQuery } from 'react-relay';
import { useRouter, useRouterActions } from 'react-resource-router';
import { HandleQueryError } from 'view/common/handle-query-error';
import { HEADER_HEIGHT } from 'view/common/styled';
import { FOOTER_HEIGHT } from 'view/layout/footer/styled';
import { LayoutBuilderContainer } from 'view/layout-next/controllers/store';
import { GenericErrorScreen } from 'view/layout-next/generic-error-screen';
import { LayoutViewRenderer } from 'view/layout-next/layout-view-renderer';
import AnalyticsContext from '@atlaskit/analytics-next/AnalyticsContext';
import { Box } from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss/xcss';
import { FireScreenEventOnMount } from '@atlassian/help-center-common-util/analytics/fire-screen-event';
import { isFetchNetworkOrClientError } from '@atlassian/help-center-common-util/fetch';
import type { FetchError } from '@atlassian/help-center-common-util/fetch/fetch';
import { useRelayResource } from '@atlassian/help-center-common-util/resource-router';
import { getTraceIdFromRelayQueryReference } from '@atlassian/help-center-common-util/retrieve-relay-traceid';
import { formatTitleForUrl } from '@atlassian/help-center-common-util/url';
import { CustomPageElementViewRenderer } from '@atlassian/help-center-custom-page-element-view-renderer';
import { useUFOComponentExperience } from '@atlassian/ufo/use-component-experience';
import type { viewCustomPageQuery } from './__generated__/viewCustomPageQuery.graphql';
import { viewCustomPageExperience } from './experiences';
import type { ViewCustomPageProps } from './types';
import { PageType } from 'view/layout-next/controllers/store/types';

const ViewCustomPage = ({ queryReference }: ViewCustomPageProps) => {
	di(
		FireScreenEventOnMount,
		DocumentTitle,
		usePreloadedQuery,
		addMetaToHead,
		useRouterActions,
		useRouter,
		HandleQueryError,
	);
	const [{ match }] = useRouter();

	const data = usePreloadedQuery<viewCustomPageQuery>(
		graphql`
			query viewCustomPageQuery(
				$helpCenterPageAri: ID!
				$cloudId: ID!
				$filter: HelpLayoutFilter
				$helpCenterAri: ID!
			) {
				helpCenter(cloudId: $cloudId) {
					helpCenterPageById(helpCenterPageAri: $helpCenterPageAri) {
						... on HelpCenterPage {
							id
							name @required(action: THROW) {
								default
							}
							description {
								default
							}
						}
						... on QueryError {
							__typename
							...handleQueryErrorFragment @dangerously_unaliased_fixme
						}
					}
				}
				helpLayout(cloudId: $cloudId) @optIn(to: ["HelpLayoutExperimentalSchema"]) {
					layoutByParentId(
						parentAri: $helpCenterPageAri
						helpCenterAri: $helpCenterAri
						filter: $filter
					) @required(action: THROW) {
						... on HelpLayout {
							...layoutViewRendererFragment @dangerously_unaliased_fixme
						}
						... on QueryError {
							__typename
							...handleQueryErrorFragment @dangerously_unaliased_fixme
						}
					}
				}
			}
		`,
		queryReference,
	);
	const { push } = useRouterActions();
	useEffect(() => {
		// We are marking the experience as success once the component mounts successfully. We are not using ExperienceSuccess in render because if  there is a error in useEffect, the experience will be wrongly marked as success even before hitting the error boundary.
		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		viewCustomPageExperience.success();
	}, []);

	const pageData = data?.helpCenter?.helpCenterPageById;
	const pageLayout = data?.helpLayout?.layoutByParentId;
	const pageTitle: string = pageData?.name?.default || '';
	const pageDescription: string = pageData?.description?.default || '';
	useEffect(() => {
		if (pageTitle) {
			const pageId = match.params.id;
			const paramPageTitle = match.params.title;
			if (pageId && paramPageTitle !== pageTitle) {
				push(`/page/${pageId}/${formatTitleForUrl(pageTitle)}`) as void;
			}
		}
	}, [pageTitle, match.params.id, match.params.title, push]);

	useEffect(() => {
		addMetaToHead('description', pageDescription);
	}, [pageDescription]);

	if (pageData?.__typename === 'QueryError') {
		return <HandleQueryError errorKey={pageData} />;
	}

	if (pageLayout?.__typename === 'QueryError') {
		return <HandleQueryError errorKey={pageLayout} />;
	}

	if (!pageLayout) {
		return null;
	}

	return (
		<AnalyticsContext
			data={{
				attributes: {
					helpCenterPageAri: pageData?.id || '',
				},
			}}
		>
			<DocumentTitle title={pageTitle}>
				<Box as="div" xcss={layoutViewerWrapper}>
					<LayoutViewRenderer layout={pageLayout} />
					<FireScreenEventOnMount />
					<NotifyExperienceSuccess location="view-custom-page" />
				</Box>
			</DocumentTitle>
		</AnalyticsContext>
	);
};

const ViewCustomPageLoader = () => {
	di(useRelayResource, NotifyExperienceFailure, isElementRendererSplitEnabled);
	const { queryReference } = useRelayResource<viewCustomPageQuery>(ViewCustomPageResource);
	useUFOComponentExperience(viewCustomPageExperience);
	const traceId = useMemo(
		() => getTraceIdFromRelayQueryReference(queryReference),
		[queryReference],
	);
	const [{ match }] = useRouter();

	// TODO: Move this to a lazy element error handler  to exclude from the main bundle
	const renderError = useCallback(
		(error: Error | FetchError) => {
			const analyticsAttributes = {
				traceId,
				screenName: ScreenName.VIEW_CUSTOM_PAGE,
				errorMessage: error.message,
				status: (error as FetchError).status,
				pageId: match.params.id || '',
			};

			if (!isFetchNetworkOrClientError(error as FetchError)) {
				viewCustomPageExperience
					.failure({
						metadata: analyticsAttributes,
					})
					.then(noop)
					.catch(noop);
				return (
					<NotifyExperienceFailure
						location="view-custom-page"
						errorMessage={error.message}
						traceId={traceId}
						status={(error as FetchError).status}
					>
						<Box xcss={errorScreenWrapper}>
							<AnalyticsContext data={{ attributes: analyticsAttributes }}>
								<GenericErrorScreen />
							</AnalyticsContext>
						</Box>
					</NotifyExperienceFailure>
				);
			}

			return (
				<Box xcss={errorScreenWrapper}>
					<AnalyticsContext data={{ attributes: analyticsAttributes }}>
						<GenericErrorScreen />
					</AnalyticsContext>
				</Box>
			);
		},
		[traceId, match.params.id],
	);
	if (!queryReference) return <FlexibleHomepageLoader />;
	return (
		<ErrorBoundary id="loader" renderError={renderError} packageName={ScreenName.VIEW_CUSTOM_PAGE}>
			<LazySuspense fallback={<FlexibleHomepageLoader />}>
				<LayoutBuilderContainer
					pageType={PageType.CUSTOM_PAGE}
					screenName={ScreenName.VIEW_CUSTOM_PAGE}
					experience={viewCustomPageExperience}
					traceId={traceId}
					ElementRenderer={
						isElementRendererSplitEnabled() ? CustomPageElementViewRenderer : undefined
					}
				>
					<ViewCustomPage queryReference={queryReference} />
				</LayoutBuilderContainer>
			</LazySuspense>
		</ErrorBoundary>
	);
};

export default withExperience(ExperienceName.CUSTOM_PAGE)(ViewCustomPageLoader);

// Added this to remove header and footer height and take the complete height for showing error
const errorScreenWrapper = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	height: `calc(100vh - ${HEADER_HEIGHT + FOOTER_HEIGHT}px)`,
});

const layoutViewerWrapper = xcss({
	position: 'relative',
	paddingBottom: 'space.400',
	flexGrow: 1,
});
